/**
 * Dummy prescription and general flags for demo/test data.
 * Shape matches Epic prescription flag format: Id, Type, AddedBy, Comments, Description, DisplayName, AddedDateTime.
 */

const DUMMY_FLAGS = [
  {
    Id: '101',
    Type: 'Regulatory',
    AddedBy: 'Admission_Coord',
    Comments: 'Patient at 48 hours; 24 hours remaining for SNF eligibility.',
    Description: 'Tracks the Medicare 3-day inpatient stay requirement for SNF coverage.',
    DisplayName: 'SNF 3-Day Rule Pending',
    AddedDateTime: '2026-01-30T08:00:00Z',
  },
  {
    Id: '102',
    Type: 'Compliance',
    AddedBy: 'Billing_Specialist',
    Comments: 'Frequency limit for DEXA scan reached.',
    Description: 'Advance Beneficiary Notice (ABN) required for services Medicare may deem non-covered.',
    DisplayName: 'ABN Required',
    AddedDateTime: '2026-01-30T09:15:22Z',
  },
  {
    Id: '103',
    Type: 'Administrative',
    AddedBy: 'System_Auto',
    Comments: 'Medicare Advantage Plan detected; verify network status.',
    Description: 'Identifies patients on Part C (Medicare Advantage) vs. Traditional Medicare.',
    DisplayName: 'Medicare Advantage (Part C)',
    AddedDateTime: '2026-01-28T14:20:10Z',
  },
  {
    Id: '104',
    Type: 'Clinical',
    AddedBy: 'Care_Manager',
    Comments: 'Post-discharge follow-up call scheduled for 48h.',
    Description: 'Alerts for Transitional Care Management (TCM) billing eligibility (99495/99496).',
    DisplayName: 'TCM Window Active',
    AddedDateTime: '2026-01-29T11:00:00Z',
  },
  {
    Id: '105',
    Type: 'Safety',
    AddedBy: 'Nurse_Lead',
    Comments: 'Patient has history of combativeness during blood draws.',
    Description: 'Alerts staff to potential behavioral risks or safety protocols.',
    DisplayName: 'Safety Alert: Behavioral',
    AddedDateTime: '2026-01-27T16:45:00Z',
  },
];

const DUMMY_PRESCRIPTION_FLAGS = [
  {
    Id: 'AOB',
    Type: 'Legal',
    AddedBy: 'Front_Desk',
    Comments: 'Standard Medicare AOB signed.',
    Description: 'Assignment of Benefits: Patient authorizes direct payment to provider.',
    DisplayName: 'AOB On File',
    AddedDateTime: '2026-01-29T09:49:38Z',
  },
  {
    Id: 'MSP',
    Type: 'Billing',
    AddedBy: 'Reg_Clerk',
    Comments: "Verify if spouse's employer insurance is primary.",
    Description: 'Medicare Secondary Payer questionnaire needs annual update.',
    DisplayName: 'MSP Verification Due',
    AddedDateTime: '2026-01-30T10:05:00Z',
  },
  {
    Id: 'AWV',
    Type: 'Preventive',
    AddedBy: 'Epic_Engine',
    Comments: 'Eligible for G0439 as of 01/01/2026.',
    Description: 'Medicare Annual Wellness Visit is due.',
    DisplayName: 'Annual Wellness Due',
    AddedDateTime: '2026-01-01T00:01:00Z',
  },
  {
    Id: 'HHA',
    Type: 'Documentation',
    AddedBy: 'Case_Worker',
    Comments: 'Face-to-Face encounter documentation required.',
    Description: 'Medicare Home Health Certification (Form 485) requires physician signature.',
    DisplayName: 'Home Health Cert Needed',
    AddedDateTime: '2026-01-30T12:30:00Z',
  },
  {
    Id: 'DME',
    Type: 'Supply',
    AddedBy: 'Dr_Admin',
    Comments: 'C-PAP supplies ordered; check CMN status.',
    Description: 'Certificate of Medical Necessity (CMN) required for DMEPOS billing.',
    DisplayName: 'DME CMN Required',
    AddedDateTime: '2026-01-29T15:20:11Z',
  },
  {
    Id: 'COB',
    Type: 'Billing',
    AddedBy: 'Finance_User',
    Comments: 'Secondary Medigap policy active.',
    Description: 'Coordination of Benefits alert for supplemental coverage.',
    DisplayName: 'Medigap Active',
    AddedDateTime: '2026-01-30T07:45:00Z',
  },
  {
    Id: 'MCR_LMT',
    Type: 'Compliance',
    AddedBy: 'Therapy_Dept',
    Comments: 'Physical Therapy cap reached; apply KX modifier.',
    Description: 'Alerts when patient approaches Medicare Part B therapy thresholds.',
    DisplayName: 'Therapy Cap Threshold',
    AddedDateTime: '2026-01-28T09:00:00Z',
  },
  {
    Id: 'hospice',
    Type: 'Clinical',
    AddedBy: 'Palliative_Team',
    Comments: 'Medicare Hospice Benefit initiated; Part B claims may deny.',
    Description: 'Indicates patient has elected the Medicare Hospice Benefit.',
    DisplayName: 'Hospice Election Active',
    AddedDateTime: '2026-01-25T14:00:00Z',
  },
  {
    Id: 'OOP_MAX',
    Type: 'Financial',
    AddedBy: 'Billing_System',
    Comments: 'Patient reached Part D donut hole.',
    Description: 'Tracking for Medicare Part D prescription drug coverage stages.',
    DisplayName: 'Part D Coverage Gap',
    AddedDateTime: '2026-01-30T13:10:00Z',
  },
  {
    Id: 'ESRD',
    Type: 'Clinical',
    AddedBy: 'Renal_Clinic',
    Comments: 'Qualifies for ESRD Medicare specialty billing.',
    Description: 'End-Stage Renal Disease status affecting Medicare eligibility/billing.',
    DisplayName: 'ESRD Status',
    AddedDateTime: '2026-01-20T11:55:00Z',
  },
];

/** All dummy prescription flag Ids for test config placeholder (e.g. AOB, MSP, AWV, A11, A22, A33). */
const DUMMY_PRESCRIPTION_FLAG_IDS = DUMMY_PRESCRIPTION_FLAGS.map((f) => f.Id);

/**
 * Returns a random subset of DUMMY_PRESCRIPTION_FLAGS (minCount to maxCount flags).
 */
function getRandomPrescriptionFlags(minCount, maxCount) {
  const min = Math.max(0, Math.min(minCount, DUMMY_PRESCRIPTION_FLAGS.length));
  const max = Math.max(min, Math.min(maxCount, DUMMY_PRESCRIPTION_FLAGS.length));
  const count = min + Math.floor(Math.random() * (max - min + 1));
  const copy = DUMMY_PRESCRIPTION_FLAGS.slice();
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy.slice(0, count);
}

/**
 * Returns a deterministic subset of DUMMY_PRESCRIPTION_FLAGS for static data (e.g. per pickupCode).
 * seed: string or number (e.g. itemId "33", orderId "34").
 */
function getPrescriptionFlagsSubsetForSeed(seed, minCount, maxCount) {
  const num = typeof seed === 'string' ? parseInt(seed, 10) || seed.length : Number(seed) || 0;
  const min = Math.max(0, Math.min(minCount, DUMMY_PRESCRIPTION_FLAGS.length));
  const max = Math.max(min, Math.min(maxCount, DUMMY_PRESCRIPTION_FLAGS.length));
  const count = min + (Math.abs(num) % (max - min + 1));
  const start = Math.abs(num) % DUMMY_PRESCRIPTION_FLAGS.length;
  const out = [];
  for (let i = 0; i < count; i++) {
    out.push(DUMMY_PRESCRIPTION_FLAGS[(start + i) % DUMMY_PRESCRIPTION_FLAGS.length]);
  }
  return out;
}

/** Map of prescription flag Id -> flag object for quick lookup. */
const DUMMY_PRESCRIPTION_FLAGS_BY_ID = DUMMY_PRESCRIPTION_FLAGS.reduce(function (acc, f) {
  acc[f.Id] = f;
  return acc;
}, {});

/** Map of DisplayName (and Id) -> Id for normalizing to API codes (API matches on external_codes by Id). */
function buildDisplayNameToIdMap() {
  var map = {};
  DUMMY_PRESCRIPTION_FLAGS.forEach(function (f) {
    if (f.Id) map[f.Id] = f.Id;
    if (f.DisplayName) map[f.DisplayName] = f.Id;
    if (f.DisplayName && f.DisplayName.toLowerCase) map[f.DisplayName.toLowerCase()] = f.Id;
  });
  return map;
}
var DISPLAY_NAME_TO_ID = buildDisplayNameToIdMap();

/**
 * Normalizes account config values to API flag codes (Ids). "AOB On File" -> "AOB" so API external_codes match.
 * Returns array of string codes to send in signature-requirements request.
 */
function accountConfigToApiCodes(accountConfig) {
  var config = normalizeAccountConfig(accountConfig || []);
  var seen = {};
  return config
    .filter(function (code) {
      return typeof code === 'string' && code.trim().length > 0;
    })
    .map(function (code) {
      var trimmed = code.trim();
      var id = DISPLAY_NAME_TO_ID[trimmed] || DISPLAY_NAME_TO_ID[trimmed.toLowerCase()] || trimmed;
      return id;
    })
    .filter(function (id) {
      if (seen[id]) return false;
      seen[id] = true;
      return true;
    });
}

/**
 * Converts account-level flag config (array of strings, e.g. signature_flags_test_config) to flag objects.
 */
function accountConfigToFlagObjects(configArray) {
  if (!Array.isArray(configArray) || configArray.length === 0) return [];
  const seen = new Set();
  return configArray
    .filter((code) => typeof code === 'string' && code.trim().length > 0)
    .map((code) => {
      const id = code.trim();
      if (seen.has(id)) return null;
      seen.add(id);
      return {
        Id: id,
        Type: id,
        DisplayName: id,
        Description: id,
        AddedBy: 'Account',
        Comments: 'Account-level flag',
        AddedDateTime: new Date().toISOString(),
      };
    })
    .filter(Boolean);
}

/**
 * Normalizes account config to an array of string codes (handles JSON string from API).
 */
function normalizeAccountConfig(accountConfig) {
  if (Array.isArray(accountConfig)) return accountConfig;
  if (typeof accountConfig === 'string') {
    try {
      var parsed = JSON.parse(accountConfig);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }
  return [];
}

/**
 * Returns flag objects that match the account's Prescription Signature Flags (signature_flags_test_config).
 * For each configured code: uses the matching DUMMY_PRESCRIPTION_FLAGS entry when available (same Id),
 * otherwise a synthetic flag object. Order follows accountConfig. No random flags.
 */
function getMatchingFlagsFromConfig(accountConfig) {
  var config = normalizeAccountConfig(accountConfig || []);
  if (config.length === 0) return [];
  var seen = new Set();
  return config
    .filter(function (code) {
      return typeof code === 'string' && code.trim().length > 0;
    })
    .map(function (code) {
      var trimmed = code.trim();
      var id = DISPLAY_NAME_TO_ID[trimmed] || DISPLAY_NAME_TO_ID[trimmed.toLowerCase()] || trimmed;
      if (seen.has(id)) return null;
      seen.add(id);
      return DUMMY_PRESCRIPTION_FLAGS_BY_ID[id] || {
        Id: id,
        Type: id,
        DisplayName: id,
        Description: id,
        AddedBy: 'Account',
        Comments: 'Account-level flag',
        AddedDateTime: new Date().toISOString(),
      };
    })
    .filter(Boolean);
}

/**
 * Builds AllFlags for demo A33 item. Prefers flags from GET /accounts/:accountId/signature-flags
 * (apiFlagCodes). Do not use accountConfig (signature_flags_test_config). When apiFlagCodes is
 * provided and non-empty, returns those; otherwise falls back to random dummy flags.
 */
function getFlagsForA33Item({ accountConfig, apiFlagCodes, dummyMin, dummyMax }) {
  if (Array.isArray(apiFlagCodes) && apiFlagCodes.length > 0) {
    return getFlagObjectsFromApiCodes(apiFlagCodes);
  }
  var config = normalizeAccountConfig(accountConfig || []);
  var matching = getMatchingFlagsFromConfig(config);
  if (matching.length > 0) return matching;
  var min = dummyMin != null ? dummyMin : 1;
  var max = dummyMax != null ? dummyMax : 2;
  return getRandomPrescriptionFlags(min, max);
}

/**
 * Maps API flag codes (from GET /accounts/:accountId/signature-flags externalCodes/label)
 * to flag objects for display. Uses DUMMY_PRESCRIPTION_FLAGS when Id matches so display
 * names match dummy data (e.g. "AOB" -> "AOB On File"). Use this to render account
 * signature flags fetched from the API, not from signature_flags_test_config.
 * @param {string[]} apiCodes - Array of flag codes from the API (e.g. ["AOB"])
 * @returns {Array<{ Id: string, DisplayName: string, ... }>}
 */
function getFlagObjectsFromApiCodes(apiCodes) {
  if (!Array.isArray(apiCodes) || apiCodes.length === 0) return [];
  var seen = {};
  return apiCodes
    .filter(function (code) {
      var id = typeof code === "string" ? code.trim() : String(code);
      if (!id || seen[id]) return false;
      seen[id] = true;
      return true;
    })
    .map(function (code) {
      var id = typeof code === "string" ? code.trim() : String(code);
      return DUMMY_PRESCRIPTION_FLAGS_BY_ID[id] || {
        Id: id,
        Type: id,
        DisplayName: id,
        Description: id,
        AddedBy: "Account",
        Comments: "Account-level flag",
        AddedDateTime: new Date().toISOString(),
      };
    });
}

module.exports = {
  DUMMY_FLAGS,
  DUMMY_PRESCRIPTION_FLAGS,
  DUMMY_PRESCRIPTION_FLAG_IDS,
  getRandomPrescriptionFlags,
  getPrescriptionFlagsSubsetForSeed,
  accountConfigToFlagObjects,
  accountConfigToApiCodes,
  getMatchingFlagsFromConfig,
  getFlagsForA33Item,
  getFlagObjectsFromApiCodes,
};



// WEBPACK FOOTER //
// ./src/components/shared/dummyPrescriptionFlags.js